import { useState } from "react";
import { Star, ArrowLeft, CheckCircle, Mail, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountDelete = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!emailValid) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!confirmed) {
      setError("Please confirm you understand this action is permanent.");
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/account/delete-request`, {
        email: email.trim(),
        reason: reason.trim(),
        platform: "web",
      });
      setSuccess(res.data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Could not submit your request. Please email support@foundstar.in and we will process it manually.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="account-delete-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-[#64748B] hover:text-[#FF69B4]"
            data-testid="account-delete-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#87CEEB]/20 flex items-center justify-center">
              <Star className="w-4 h-4 text-[#FF69B4]" />
            </div>
            <span className="font-outfit font-bold text-lg text-[#1E293B]">
              Delete Account
            </span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
          <h1 className="font-outfit font-bold text-2xl md:text-3xl text-[#1E293B] mb-2">
            Delete Your FoundStar Account
          </h1>
          <p className="text-sm text-[#64748B] mb-6">
            Developer: FoundStar &middot; App: FoundStar (in.foundstar.app)
          </p>

          {/* What gets deleted */}
          <section className="mb-8">
            <h2 className="font-semibold text-lg text-[#1E293B] mb-3">
              What we will delete
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-[#475569]">
              <li>Your profile information (name, photo, bio, social links)</li>
              <li>Your login credentials (email, password, phone)</li>
              <li>Your uploaded portfolio photos and videos</li>
              <li>Your chat messages and notifications</li>
              <li>Your saved brands / creators and preferences</li>
              <li>Location data and device push tokens</li>
            </ul>
          </section>

          {/* What we may retain */}
          <section className="mb-8">
            <h2 className="font-semibold text-lg text-[#1E293B] mb-3">
              What we may retain (as required by law)
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-[#475569]">
              <li>
                Transaction records related to payments processed through Razorpay
                (retained for up to 7 years for GST &amp; accounting compliance under
                the Indian IT Act &amp; Companies Act).
              </li>
              <li>
                Anonymised, aggregated analytics that cannot be linked back to you.
              </li>
              <li>
                Records of past dealings on active or disputed contracts until they
                are resolved.
              </li>
            </ul>
          </section>

          {/* Timeline */}
          <section className="mb-8 bg-[#FFF5F9] border border-[#FF69B4]/20 rounded-xl p-4">
            <h2 className="font-semibold text-lg text-[#1E293B] mb-2">
              Timeline
            </h2>
            <p className="text-[#475569]">
              We process deletion requests within <strong>30 days</strong>. You
              will receive a confirmation email at the address you provide once
              your data has been permanently removed. If you have a
              dispute-resolution or refund open with us, deletion may be paused
              until that case is closed.
            </p>
          </section>

          {/* Success state */}
          {success ? (
            <div
              className="bg-[#F0FDF4] border border-[#22C55E]/30 rounded-xl p-6 text-center"
              data-testid="account-delete-success"
            >
              <CheckCircle className="w-12 h-12 text-[#22C55E] mx-auto mb-3" />
              <h3 className="font-semibold text-xl text-[#1E293B] mb-2">
                Request received
              </h3>
              <p className="text-[#475569] mb-3">{success.message}</p>
              <p className="text-xs text-[#64748B]">
                Reference:{" "}
                <span className="font-mono">{success.request_id}</span>
              </p>
              <Button
                onClick={() => navigate("/")}
                className="mt-6 bg-[#FF69B4] hover:bg-[#FF1493] text-white"
                data-testid="account-delete-home-btn"
              >
                Back to home
              </Button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
              data-testid="account-delete-form"
            >
              <div>
                <label className="block text-sm font-medium text-[#1E293B] mb-1">
                  Registered email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="account-delete-email-input"
                  className="w-full px-4 py-2.5 rounded-lg border border-[#E2E8F0] focus:border-[#FF69B4] focus:outline-none focus:ring-2 focus:ring-[#FF69B4]/20 text-[#1E293B]"
                />
                <p className="text-xs text-[#64748B] mt-1">
                  Use the same email you signed up with so we can locate your
                  account.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1E293B] mb-1">
                  Reason (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder="Tell us why you are leaving. This helps us improve — but you are not required to answer."
                  data-testid="account-delete-reason-input"
                  className="w-full px-4 py-2.5 rounded-lg border border-[#E2E8F0] focus:border-[#FF69B4] focus:outline-none focus:ring-2 focus:ring-[#FF69B4]/20 text-[#1E293B] resize-none"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-[#FEE2E2] bg-[#FEF2F2]">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  data-testid="account-delete-confirm-checkbox"
                  className="mt-1 accent-[#EF4444] cursor-pointer"
                />
                <span className="text-sm text-[#7F1D1D]">
                  <AlertTriangle className="w-4 h-4 inline mr-1 -mt-0.5" />
                  I understand this action is <strong>permanent</strong> and
                  will remove my FoundStar profile, portfolio, chats and login
                  credentials. This cannot be undone.
                </span>
              </label>

              {error && (
                <div
                  className="bg-[#FEF2F2] border border-[#FEE2E2] text-[#B91C1C] text-sm rounded-lg p-3"
                  data-testid="account-delete-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting || !emailValid || !confirmed}
                data-testid="account-delete-submit-btn"
                className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white font-semibold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting..." : "Request Account Deletion"}
              </Button>

              <p className="text-xs text-[#64748B] text-center">
                Prefer email? Write to{" "}
                <a
                  href="mailto:support@foundstar.in?subject=Account%20deletion%20request"
                  className="text-[#FF69B4] hover:underline"
                >
                  <Mail className="w-3 h-3 inline -mt-0.5" />{" "}
                  support@foundstar.in
                </a>{" "}
                with the subject "Account deletion request".
              </p>
            </form>
          )}
        </div>

        {/* SEO / crawlable text block for Play Console reviewers */}
        <div className="max-w-4xl mx-auto mt-6 text-xs text-[#94A3B8] text-center">
          FoundStar &middot; App package: in.foundstar.app &middot; Data
          controller: FoundStar, India &middot; Contact:{" "}
          <a
            href="mailto:support@foundstar.in"
            className="text-[#FF69B4] hover:underline"
          >
            support@foundstar.in
          </a>
        </div>
      </main>
    </div>
  );
};

export default AccountDelete;
